import React, { useState, useCallback } from 'react';
import { buildNewsletterHeaderHtml, newsletterHeaderStyles } from '../constants';

interface Props {
    API_BASE: string;
    authHeaders: () => Record<string, string>;
}

export const NewsletterSection: React.FC<Props> = ({ API_BASE, authHeaders }) => {
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const [signature, setSignature] = useState('');
    const [recipientCount, setRecipientCount] = useState<number | null>(null);
    const [loadingCount, setLoadingCount] = useState(false);
    const [sending, setSending] = useState(false);
    const [status, setStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
    const [showConfirm, setShowConfirm] = useState(false);
    const [showPreview, setShowPreview] = useState(false);

    const fetchRecipientCount = useCallback(async () => {
        setLoadingCount(true);
        setRecipientCount(null);
        try {
            const res = await fetch(`${API_BASE}/mailing/recipient-count`, {
                method: 'GET',
                headers: authHeaders(),
            });
            if (!res.ok) throw new Error('Failed to fetch recipient count');
            const data = await res.json();
            setRecipientCount(data.count ?? 0);
        } catch {
            setRecipientCount(null);
        } finally {
            setLoadingCount(false);
        }
    }, [API_BASE, authHeaders]);

    const handleSend = async () => {
        setShowConfirm(false);
        setSending(true);
        setStatus(null);
        try {
            const res = await fetch(`${API_BASE}/mailing/send`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({ subject, body, signature }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.message || 'Failed to send newsletter');
            setStatus({ type: 'success', message: `Newsletter sent to ${data.sent} recipient${data.sent === 1 ? '' : 's'}.` });
        } catch (err: any) {
            setStatus({ type: 'error', message: err.message || 'Failed to send newsletter' });
        } finally {
            setSending(false);
        }
    };

    const previewHtml = buildPreviewHtml(subject, body, signature);
    const canSend = subject.trim() && body.trim();

    return (
        <div>
            <h2>Send Newsletter</h2>
            <p style={{ color: '#555', marginTop: 0 }}>
                Compose and send an email to all users who have opted in to the Eluum mailing list.
                Emails are sent from <strong>updates@eluum.com</strong> with consistent Eluum branding.
            </p>

            <div className="form-group">
                <label htmlFor="nl-subject"><strong>Subject</strong></label>
                <input
                    id="nl-subject"
                    type="text"
                    className="dashboard-input"
                    placeholder="Email subject line"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    style={{ width: '100%' }}
                />
            </div>

            <div className="form-group" style={{ marginTop: 14 }}>
                <label htmlFor="nl-body">
                    <strong>Body</strong>
                    <span style={{ fontWeight: 400, color: '#888', marginLeft: 8, fontSize: 13 }}>
                        HTML supported · links: [text](https://…), a bare https://… URL, or &lt;a href&gt;
                    </span>
                </label>
                <textarea
                    id="nl-body"
                    className="dashboard-input"
                    placeholder="<p>Hello Eluum community,</p>&#10;<p>Here's what's new this month…</p>"
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    rows={10}
                    style={{ width: '100%', fontFamily: 'monospace', fontSize: 13 }}
                />
            </div>

            <div className="form-group" style={{ marginTop: 14 }}>
                <label htmlFor="nl-signature">
                    <strong>Signature</strong>
                    <span style={{ fontWeight: 400, color: '#888', marginLeft: 8, fontSize: 13 }}>optional · plain text</span>
                </label>
                <textarea
                    id="nl-signature"
                    className="dashboard-input"
                    placeholder="The Eluum Team"
                    value={signature}
                    onChange={(e) => setSignature(e.target.value)}
                    rows={3}
                    style={{ width: '100%' }}
                />
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 18, flexWrap: 'wrap', alignItems: 'center' }}>
                <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowPreview(true)}
                    disabled={!canSend}
                >
                    Preview Email
                </button>
                <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={fetchRecipientCount}
                    disabled={loadingCount}
                >
                    {loadingCount ? 'Loading…' : 'Check Recipients'}
                </button>
                {recipientCount !== null && (
                    <span style={{ color: '#374151', fontSize: 14 }}>
                        <strong>{recipientCount}</strong> opted-in recipient{recipientCount === 1 ? '' : 's'}
                    </span>
                )}
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => setShowConfirm(true)}
                    disabled={!canSend || sending}
                    style={{ marginLeft: 'auto' }}
                >
                    {sending ? 'Sending…' : 'Send Newsletter'}
                </button>
            </div>

            {status && (
                <p className={`status ${status.type === 'error' ? 'status-error' : ''}`} style={{ marginTop: 12 }}>
                    {status.message}
                </p>
            )}

            {/* Send confirmation dialog */}
            {showConfirm && (
                <div style={overlayStyle}>
                    <div style={modalStyle}>
                        <h3 style={{ marginTop: 0 }}>Send Newsletter?</h3>
                        <p>
                            This will send <strong>"{subject}"</strong> to{' '}
                            {recipientCount !== null
                                ? <strong>{recipientCount} opted-in recipient{recipientCount === 1 ? '' : 's'}</strong>
                                : 'all opted-in recipients'}.
                        </p>
                        <p style={{ color: '#6b7280', fontSize: 13 }}>This action cannot be undone.</p>
                        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
                            <button className="btn btn-secondary" onClick={() => setShowConfirm(false)}>Cancel</button>
                            <button className="btn btn-primary" onClick={handleSend}>Send</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Preview dialog */}
            {showPreview && (
                <div style={overlayStyle} onClick={() => setShowPreview(false)}>
                    <div
                        style={{ ...modalStyle, maxWidth: 680, width: '90vw', maxHeight: '85vh', overflow: 'auto' }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                            <h3 style={{ margin: 0 }}>Email Preview</h3>
                            <button className="btn btn-secondary" style={{ fontSize: 13 }} onClick={() => setShowPreview(false)}>
                                Close
                            </button>
                        </div>
                        <div
                            style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}
                            dangerouslySetInnerHTML={{ __html: previewHtml }}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Mirrors linkifyLine in src/lambdas/mailing-service/index.ts — keep in sync.
const LINK_STYLE = 'color:#667eea;text-decoration:underline;';

/**
 * Turn link shorthand in plain-text bodies into clickable anchors:
 *   [link text](https://example.com)  ->  <a href>link text</a>
 *   https://example.com               ->  <a href>https://example.com</a>
 * Existing <a href="..."> tags are left alone (the lookbehinds skip URLs that
 * sit in an attribute or directly inside a tag).
 */
function linkifyLine(line: string): string {
    return line
        .replace(
            /\[([^\]\n]+)\]\((https?:\/\/[^\s)]+|mailto:[^\s)]+)\)/g,
            (_m, text, url) => `<a href="${url}" style="${LINK_STYLE}">${text}</a>`,
        )
        .replace(/(?<!["'>=(])\bhttps?:\/\/[^\s<>"']+/g, (match) => {
            const url = match.replace(/[.,;:!?)]+$/, '');
            const trailing = match.slice(url.length);
            return `<a href="${url}" style="${LINK_STYLE}">${url}</a>${trailing}`;
        });
}

// Mirrors renderBodyHtml in src/lambdas/mailing-service/index.ts — keep in sync.
// Plain-text bodies render as block-level <div>s (one per line) so line breaks
// survive Outlook on the web, which strips bare <br> tags.
function renderBodyHtml(raw: string): string {
    const looksLikeHtml = /<(p|div|table|ul|ol|h[1-6]|blockquote)[\s>]/i.test(raw);
    if (looksLikeHtml) {
        return raw.replace(/\n/g, '<br>');
    }
    return raw
        .split('\n')
        .map((line) =>
            line.trim() === ''
                ? '<div style="line-height:16px;font-size:0;">&nbsp;</div>'
                : `<div style="margin:0;">${linkifyLine(line)}</div>`,
        )
        .join('');
}

function buildPreviewHtml(subject: string, bodyHtml: string, signature: string): string {
    const signatureBlock = signature
        ? `<div style="margin-top:24px;padding-top:16px;border-top:1px solid #e5e7eb;font-size:14px;color:#374151;">${signature.replace(/\n/g, '<br>')}</div>`
        : '';

    return `<style>${newsletterHeaderStyles}</style>
<div style="background:#f3f4f6;padding:24px 16px;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;">
  <div style="max-width:600px;margin:0 auto;background:#fff;border-radius:12px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,0.08);">
    ${buildNewsletterHeaderHtml()}
    <div style="padding:32px 36px;color:#1f2937;font-size:15px;line-height:1.7;">
      ${bodyHtml ? renderBodyHtml(bodyHtml) : '<p style="color:#9ca3af;font-style:italic;">Body will appear here…</p>'}
      ${signatureBlock}
    </div>
    <div style="background:#f9fafb;padding:16px 36px;border-top:1px solid #e5e7eb;text-align:center;font-size:12px;color:#9ca3af;">
      <p style="margin:0 0 4px;">You're receiving this because you opted in to the Eluum mailing list.</p>
      <p style="margin:0;"><a href="#" style="color:#667eea;text-decoration:none;">Unsubscribe</a> · <a href="#" style="color:#667eea;text-decoration:none;">eluum.com</a></p>
      <p style="margin:8px 0 0;">© ${new Date().getFullYear()} Eluum Corporation. All rights reserved.</p>
    </div>
  </div>
</div>`;
}

const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0, left: 0,
    width: '100vw', height: '100vh',
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
};

const modalStyle: React.CSSProperties = {
    background: '#fff',
    padding: '28px 32px',
    borderRadius: 10,
    maxWidth: 460,
    width: '90vw',
    boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
};
